import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AuthProvider, Me } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { PROVIDER_LABEL } from "../lib/auth";
import { Avatar, useScrollLock } from "./ui";
import { CloseIcon } from "./Icons";
import { ProviderIcon } from "./SignIn";
import { account } from "@pagemates/shared/copy";

const txt = account.avatarPicker;

/*
 * Profile picture (PLAN.md §Profile pictures). The avatar at the top of the
 * profile carries a pencil; it opens a sheet listing the picture from each
 * linked sign-in, an upload, and (when uploaded) remove. The browser
 * centre-crops and shrinks an upload to a 256px WebP before it leaves the
 * device, so the server stores a few kilobytes.
 */
const SIZE = 256;

export async function shrink(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement("canvas");
  canvas.width = SIZE; canvas.height = SIZE;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, SIZE, SIZE);
  bitmap.close();
  const webp = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/webp", 0.85));
  if (webp && webp.type === "image/webp") return webp;
  const jpeg = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.85));
  if (!jpeg) throw new Error("Couldn't read that image.");
  return jpeg;
}

/** The avatar with its pencil. Tap to open the chooser. */
export function EditableAvatar({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const name = me.displayName ?? me.name;
  return (
    <>
      <button type="button" className="av-edit" aria-label={txt.changeYourPicture} onClick={() => setOpen(true)}>
        <Avatar name={name} image={me.image} size="lg" />
        <span className="pencil" aria-hidden="true">✎</span>
      </button>
      {open && <AvatarSheet me={me} onClose={() => setOpen(false)} />}
    </>
  );
}

function AvatarSheet({ me, onClose }: { me: Me; onClose: () => void }) {
  const qc = useQueryClient();
  const choices = useQuery({ queryKey: ["avatar-choices"], queryFn: api.avatarChoices, staleTime: 5 * 60_000 });
  const file = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const done = async () => { await qc.invalidateQueries({ queryKey: ["me"] }); onClose(); };
  const pick = useMutation({ mutationFn: (p: AuthProvider) => api.useProviderPicture(p), onSuccess: done });
  const up = useMutation({ mutationFn: async (f: File) => api.uploadAvatar(await shrink(f)), onSuccess: done, onError: (e) => setErr(e instanceof Error && !("status" in e) ? e.message : null) });
  const rm = useMutation({ mutationFn: api.removeAvatar, onSuccess: done });
  const busy = pick.isPending || up.isPending || rm.isPending;
  useScrollLock();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const list = choices.data ?? [];
  const name = me.displayName ?? me.name;

  return (
    <>
      <div className="modal-dim" onClick={onClose} />
      <div className="modal av-sheet" role="dialog" aria-label={txt.profilePicture}>
        <div className="grab" />
        <div className="head"><b>{txt.selectAnImage}</b><button type="button" aria-label={txt.close} onClick={onClose}><CloseIcon /></button></div>
        <div className="av-body">
          <button type="button" className="av-upload" disabled={busy} onClick={() => file.current?.click()}>
            <span className="ico" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="14" height="14" rx="2" /><path d="M3 15l4-4 4 4 3-3 3 3" /><circle cx="9" cy="9" r="1.2" /><path d="M20 3v6M17 6h6" /></svg>
            </span>
            <b>{up.isPending ? txt.uploading : txt.uploadAPhoto}</b>
            <small>{txt.croppedSquareAndShrunk}</small>
          </button>
          <input ref={file} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setErr(null); up.mutate(f); } }} />
          {(choices.isPending || list.length > 0) && (
            <div className="av-from">
              <b>{txt.fromYourSignIns}</b>
              <span className="muted small">{choices.isPending ? txt.looking : txt.thePictureEachOne}</span>
              <div className="av-row">
                {list.map((c) => (
                  <button key={c.provider} type="button" className={`av-opt${me.image === c.url ? " on" : ""}`} disabled={busy} title={txt.useYourPicture(PROVIDER_LABEL[c.provider])} aria-label={txt.useYourPicture(PROVIDER_LABEL[c.provider])} onClick={() => pick.mutate(c.provider)}>
                    <img src={c.url} alt="" /><span className="pi"><ProviderIcon p={c.provider} /></span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {me.avatarSource === "upload" && (
            <button type="button" className="linkish small muted" style={{ alignSelf: "flex-start" }} disabled={busy} onClick={() => rm.mutate()}>{txt.removeYourPhoto(list.length ? txt.andUseYourPicture(PROVIDER_LABEL[list[0]!.provider]) : "")}</button>
          )}
          {(err || pick.isError || up.isError || rm.isError) && <p className="error small">{err ?? describeError(pick.error ?? up.error ?? rm.error)}</p>}
        </div>
      </div>
    </>
  );
}
